import { LocaleData, registerLocale, getName } from 'i18n-iso-countries';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';

import { registerLocaleData } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import angularLocaleDe from '@angular/common/locales/de';
import angularLocaleEn from '@angular/common/locales/en';
import angularLocaleFr from '@angular/common/locales/fr';
import angularLocaleIt from '@angular/common/locales/it';
import angularLocaleNl from '@angular/common/locales/nl';
import { inject, isDevMode, LOCALE_ID, Provider } from '@angular/core';

import { Translation, TranslocoLoader, TranslocoOptions, TranslocoTestingOptions } from '@jsverse/transloco';

const SUPPORTED_LANGS: { lang: string; data: any }[] = [
  { lang: 'fr', data: angularLocaleFr },
  { lang: 'en', data: angularLocaleEn },
  { lang: 'nl', data: angularLocaleNl },
  { lang: 'de', data: angularLocaleDe },
  { lang: 'it', data: angularLocaleIt },
];

SUPPORTED_LANGS.forEach(({ lang, data }) => registerLocaleData(data, lang));

const getTranslatedCountries = (value: LocaleData, lang: string) => {
  registerLocale(value);
  const countries: Record<string, string[] | string> = {};
  Object.keys(value.countries).forEach((country) => (countries[country] = getName(country, lang, { select: 'alias' })));
  return countries;
};

class TranslocoHttpLoader implements TranslocoLoader {
  private http = inject(HttpClient);

  getCountries(lang: string) {
    return this.http.get<LocaleData>(`/countries/${lang}.json`).pipe(map((vendor) => getTranslatedCountries(vendor, lang)));
  }

  getTranslation(lang: string) {
    return forkJoin([this.http.get<Translation>(`/i18n/${lang}.json`), this.getCountries(lang)]).pipe(
      map(([translation, countries]) => ({ ...translation, countries })),
    );
  }
}

export const withWattSonTranslocoOptions = (): TranslocoOptions => {
  return {
    config: {
      availableLangs: SUPPORTED_LANGS.map((l) => l.lang),
      defaultLang: 'fr',
      // Remove this option if your application doesn't support changing language in runtime.
      reRenderOnLangChange: true,
      prodMode: !isDevMode(),
      fallbackLang: 'en',
    },
    loader: TranslocoHttpLoader,
  };
};

const loadTranslationsForTest = (lang: string) => {
  return import(`../../../../public/i18n/${lang}.json`).then((translation) => {
    return import(`i18n-iso-countries/langs/${lang}.json`).then((vendor) => ({
      ...translation,
      countries: getTranslatedCountries(vendor, lang),
    }));
  });
};

export const withWattSonTestTranslocoOptions = (): TranslocoTestingOptions => {
  const langs = SUPPORTED_LANGS.map((l) => l.lang).reduce((acc, lang) => {
    acc[lang] = loadTranslationsForTest(lang);
    return acc;
  }, {});
  return {
    langs,
    translocoConfig: {
      availableLangs: SUPPORTED_LANGS.map((l) => l.lang),
      defaultLang: 'fr',
      reRenderOnLangChange: true,
    },
    preloadLangs: true,
  };
};

export const provideLocalId = (): Provider => {
  return { provide: LOCALE_ID, useValue: 'fr' };
};
